import { useEffect, useState } from "react"
import { useSnackbar } from "notistack"
import { faEnvelope, faMessageSmile } from "@fortawesome/pro-light-svg-icons"
import { LocalizedStrict } from "../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../modules/localization/components/AppLocalizationProvider"
import ChatSupportAnim from "../../../../Intercom/assets/chat-support-lottie-anim.json"
import { IntercomStatus } from "../../../../Intercom/intercomTypes"
import { ConsentDialog } from "../../consentDialog/ConsentDialog"
import { PIIItem } from "../../consentDialog/consentDialogTypes"
import { useExternalLinks } from "../../../../hooks/useAppLinks"
import { useIntercom } from "../../../../Intercom/useIntercom"
import { Logger } from "../../../../../lib/logging"
import { UserSettingElementType } from "../../../../../modules/users/userTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { saveAdminUserSettings, UserQuery } from "../../../../../modules/users/userApi"
import { useAppSelector } from "../../../../../modules/store/storeHooks"
import { selectAuthenticatedUser } from "../../../../../modules/login/loginSelectors"
import GeneralErrorAnim from "../../../../assets/general-error-lottie-anim.json"
import { MessageDialog } from "../../MessageDialog"
import { LoadingDialog } from "../../LoadingDialog"
import { HelpSection } from "./HelpSection"
import { HelpCard } from "./HelpCard"

const PRIVACY_POLICY_URL = "https://www.intercom.com/legal/privacy"

export const SupportContent = () => {
    const user = useAppSelector(selectAuthenticatedUser)
    const [askConsent, setAskConsent] = useState(false)
    const [showError, setShowError] = useState(false)
    const [waitingForIntercom, setWaitingForIntercom] = useState(false)

    const intercomContext = useIntercom()
    const logger = new Logger("customer-service")
    const { enqueueSnackbar } = useSnackbar()
    const { l10n } = useAppLocalization()

    function handleChatSupportClick() {
        logger.info(`Attempt to open Intercom chat (status: ${intercomContext.status})`)
        if (intercomContext.status === IntercomStatus.LoggedIn) {
            intercomContext.intercom.show()
        } else if (intercomContext.status === IntercomStatus.Ready) {
            setAskConsent(true)
        } else if (intercomContext.status === IntercomStatus.Error) {
            setShowError(true)
        } else {
            // Initializing: with no valid REACT_APP_INTERCOM_APPID the widget never boots, so the
            // status is stuck here and show() would never fire. Tell the user instead of no-oping.
            enqueueSnackbar(l10n.getString("intercom-unavailable-snackbar"), { variant: "warning" })
        }
    }

    const { mutateAsync: callUpdateUser } = useApiMutation(saveAdminUserSettings, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [UserQuery.ADMIN_DETAILS] })
            setAskConsent(false)
        },
    })

    function handleConsentClick(consented: boolean) {
        logger.info(`User consented: ${consented}`)
        if (consented) {
            setWaitingForIntercom(true)
            if (intercomContext.status === IntercomStatus.Ready) intercomContext.setUserConsented()
            callUpdateUser({
                userSettings: {
                    elementType: UserSettingElementType.TOGGLE,
                    name: "INTERCOM_CONSENT",
                    value: Boolean(consented).toString(),
                },
            })
        } else {
            setAskConsent(false)
        }
    }

    useEffect(() => {
        if (waitingForIntercom) {
            if (intercomContext.status === IntercomStatus.LoggedIn || intercomContext.status === IntercomStatus.Error) {
                setWaitingForIntercom(false)
            }
            if (intercomContext.status === IntercomStatus.LoggedIn) {
                intercomContext.intercom.show()
            } else if (intercomContext.status === IntercomStatus.Error) {
                setShowError(true)
            }
        }
    }, [intercomContext.status])

    const handleExternalLink = useExternalLinks()

    return (
        <LocalizedStrict id="help-dialog-support" attrs={{ title: true }}>
            <HelpSection title="Support">
                <LocalizedStrict id="help-dialog-email-support" attrs={{ title: true, description: true }}>
                    <HelpCard
                        icon={faEnvelope}
                        title="Email support"
                        description="If you still need help, you can email us here."
                        handleCardClick={() => handleExternalLink("mailto:support@facilitynet.dk")}
                    />
                </LocalizedStrict>
                <LocalizedStrict id="help-dialog-chat-support" attrs={{ title: true, description: true }}>
                    <HelpCard
                        icon={faMessageSmile}
                        title="Chat support"
                        description="For quick questions, you can chat with our customer support team."
                        handleCardClick={handleChatSupportClick}
                        // shouldRender={chatSupportEnabled}
                    />
                </LocalizedStrict>
                <LocalizedStrict
                    id="consent-dialog"
                    attrs={{ title: true, message: true, skipLabel: true, consentLabel: true }}
                >
                    <ConsentDialog
                        name="intercom"
                        title="Allow Intercom"
                        message="We make use of Intercom for our chat support. If you consent, we will share the data below with Intercom."
                        lottieAnim={ChatSupportAnim}
                        piiItems={[PIIItem.Name, PIIItem.EmailAddress, PIIItem.UserId, PIIItem.CustomerSupport]}
                        privacyPolicyUrl={PRIVACY_POLICY_URL}
                        skipLabel="Do not accept"
                        consentLabel="Accept"
                        open={askConsent}
                        onClose={() => setAskConsent(false)}
                        // savingConsent={saving}
                        savingConsent={false}
                        onButtonClick={handleConsentClick}
                    />
                </LocalizedStrict>
                <LocalizedStrict id="intercom-error-dialog" attrs={{ title: true, message: true, buttonLabel: true }}>
                    <MessageDialog
                        name="intercom-error"
                        title="Chat fejlede"
                        message="Indlæsning af chat support mislykkedes. Du kan i stedet sende en e-mail til support."
                        buttonLabel="Forstået"
                        lottieAnim={GeneralErrorAnim}
                        open={showError}
                        onClose={() => setShowError(false)}
                    />
                </LocalizedStrict>
                <LocalizedStrict id="intercom-loading-dialog" attrs={{ description: true }}>
                    <LoadingDialog loading={waitingForIntercom} description="Chat indlæser..." />
                </LocalizedStrict>
            </HelpSection>
        </LocalizedStrict>
    )
}
